import { Router, Switch } from "react-router-dom";
import { authList } from "./routing/authList";
import CustomRoute from "./routing/CustomRoute/CustomRoute";
import { routeList } from "./routing/routeList";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./config/store";
import "./App.css";
import "@mdi/font/css/materialdesignicons.css";
import ProtectedRoute from "./routing/ProtectRoute/ProtectedRoute";
import toast, { Toaster } from "react-hot-toast";
import React, { useEffect, useState } from "react";
import { onMessageListener, requestPermission } from "./firebase";
import MetaPixel from "./utils/metaPixel";
import { history } from "./index";
import WhatsAppButton from "./components/WhatsAppButton/WhatsAppButton";
import { HelmetProvider, Helmet } from "react-helmet-async";
import getHostSubdomain from "./helpers/getHostSubdomain";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { registerServiceWorker, checkServiceWorkerStatus } from "./utils/serviceWorkerHelper";

const ToastDisplay = ({ title, body }) => {
  return (
    <div>
      <p>
        <b>{title}</b>
      </p>
      <p>{body}</p>
    </div>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      cacheTime: 0, // No cache by default
      staleTime: 0, // Data langsung dianggap stale
      refetchOnMount: true, // Refetch di background saat component mount
      refetchOnReconnect: true, // Refetch saat koneksi kembali
      refetchInterval: false, // Tidak auto refetch berkala
    },
  },
});

const queryPersister = createSyncStoragePersister({
  storage: window.localStorage,
});

function App() {
  const [notification, setNotification] = useState({ title: "", body: "" });

  useEffect(() => {
    // Menginisialisasi notifikasi hanya pada awal aplikasi
    const initNotifications = async () => {
      try {
        // Check service worker status first
        const status = await checkServiceWorkerStatus();
        console.log("Service Worker Status:", status);

        // Register service worker dengan error handling yang lebih baik
        try {
          const registration = await registerServiceWorker("/firebase-messaging-sw.js");

          if (registration) {
            console.log("✅ Service Worker siap digunakan");
          } else {
            console.warn("⚠️ Service Worker tidak dapat diregistrasi, notifikasi tidak akan berfungsi");
            return null;
          }
        } catch (swError) {
          console.error("❌ Service Worker registration failed:", swError);
          // Don't throw, just log - allow app to continue without notifications
          return null;
        }

        const permission = await requestPermission();
        console.log(`Notification permission : ${permission}`);

        // Setup listener untuk menerima notifikasi saat aplikasi aktif
        const unsubscribe = onMessageListener((payload) => {
          if (payload) {
            console.log("Notifikasi diterima:", payload);
            setNotification({
              title: payload?.notification?.title,
              body: payload?.notification?.body,
            });
          }
        });

        // Cleanup listener saat komponen unmount
        return unsubscribe;
      } catch (error) {
        console.log("Error saat inisialisasi notifikasi: ", error);
        return null;
      }
    };

    const cleanup = initNotifications();

    // Return cleanup function
    return () => {
      if (cleanup && typeof cleanup.then === "function") {
        cleanup.then((unsubscribe) => {
          if (typeof unsubscribe === "function") {
            unsubscribe();
          }
        });
      }
    };
  }, []);

  const notify = (title, body) => toast(<ToastDisplay title={title} body={body} />);

  useEffect(() => {
    if (notification?.title) {
      notify(notification?.title, notification?.body);
    }
  }, [notification]);

  return (
    <>
      <HelmetProvider>
        {getHostSubdomain() !== "global" ? (
          <Helmet>
            {/* Subdomain seller exclusive (mis. close bid) tidak boleh diindeks Google. */}
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
        ) : null}
        <PersistQueryClientProvider client={queryClient} persistOptions={{ persister: queryPersister }}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <MetaPixel />
              <Router history={history}>
                <Switch>
                  {authList.map((route, idx) => (
                    <ProtectedRoute
                      key={idx}
                      path={route.path}
                      // exact={route.exact}
                      layout={route.layout}
                      isLogged={route.isLogged}
                      page={route.component}
                    />
                  ))}
                  {routeList.map((route, idx) => (
                    <CustomRoute
                      key={idx}
                      path={route.path}
                      // exact={route.exact}
                      layout={route.layout}
                      page={route.component}
                    />
                  ))}
                </Switch>
                <Toaster />
                <WhatsAppButton />
              </Router>
            </PersistGate>
          </Provider>
        </PersistQueryClientProvider>
      </HelmetProvider>
    </>
  );
}

export default App;
